/**
 * Shared inactivity arithmetic for the 30-minute idle logout.
 *
 * Exists because two call sites — `App.tsx` (mount check) and `AppLogout` (timer tick)
 * — each did this math inline and each broke when `lastUserAction` was absent:
 *
 *  - `App.tsx` used `Number(safeLocalStorage.get('lastUserAction'))`. `safeLocalStorage.get`
 *    returns the raw string or `null`, and `Number(null)` is `0`, so
 *    `now - 0 > TIMEOUT` was ALWAYS true. Since every logout path calls
 *    `safeLocalStorage.clear()`, one logout guaranteed the next mount logged out again.
 *  - `AppLogout` used `JSON.parse(safeLocalStorage.get('lastUserAction')) + TIMEOUT`.
 *    `JSON.parse(null)` is `null` and `null + 1800000` is `1800000`, which is always
 *    `<= Date.now()` — an immediate logout on the next tick.
 *
 * A missing key means "no evidence of inactivity", which must restart the window
 * rather than resolve to "idle since the epoch".
 */

/** 30 minutes, matching the value both call sites used. */
export const INACTIVITY_TIMEOUT_MS = 1800000

/** localStorage key holding the last-activity timestamp, written as `JSON.stringify(millis)`. */
export const LAST_USER_ACTION_KEY = 'lastUserAction'

/**
 * Parse a stored `lastUserAction` timestamp, or `null` when it is absent/unusable.
 * Accepts the raw string `safeLocalStorage.get` returns (values are written with
 * `JSON.stringify(moment().valueOf())`, i.e. a bare numeric string).
 */
export function parseLastUserAction(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? raw : null
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Has the user been idle longer than the timeout?
 * Returns `false` when there is no recorded activity — absence of evidence is not
 * evidence of inactivity, and treating it as such is what made logouts self-perpetuating.
 */
export function isInactive(raw: unknown, now: number): boolean {
  const lastAction = parseLastUserAction(raw)
  if (lastAction === null) {
    return false
  }
  return now - lastAction > INACTIVITY_TIMEOUT_MS
}

/**
 * Milliseconds remaining before the idle timeout fires. A missing timestamp restarts
 * the full window, so the timer re-arms instead of firing immediately.
 */
export function msUntilInactive(raw: unknown, now: number): number {
  const lastAction = parseLastUserAction(raw)
  if (lastAction === null) {
    return INACTIVITY_TIMEOUT_MS
  }
  return Math.max(0, lastAction + INACTIVITY_TIMEOUT_MS - now)
}
